// src/hooks/useMemberProfile.js
import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { getPlan } from "../data/plans";

/**
 * Returns:
 * - user: supabase user or null
 * - profile: profile row or null
 * - planKey: "free" | "regular" | ...
 * - planObj: from getPlan()
 * - loading / error
 */
export function useMemberProfile() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [planKey, setPlanKey] = useState("free");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadProfile = useCallback(async (u) => {
    try {
      setError("");
      if (!u?.id) {
        setProfile(null);
        setPlanKey("free");
        return;
      }

      const { data, error: qErr } = await supabase
        .from("profiles")
        .select("id,email,display_name,role,plan,discount_level,created_at,updated_at")
        .eq("id", u.id)
        .maybeSingle();

      if (qErr) {
        // Don’t block UI — just fall back to free
        setProfile(null);
        setPlanKey(u?.user_metadata?.plan || "free");
        return;
      }

      setProfile(data || null);
      setPlanKey((data?.plan || u?.user_metadata?.plan || "free").toLowerCase());
    } catch (e) {
      setError(e?.message || "Profile load failed");
      setProfile(null);
      setPlanKey("free");
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        const { data } = await supabase.auth.getSession();
        const u = data?.session?.user ?? null;

        if (!mounted) return;
        setUser(u);
        await loadProfile(u);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Session load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (_evt, session) => {
      const u = session?.user ?? null;
      if (!mounted) return;

      setUser(u);
      setLoading(true);
      await loadProfile(u);
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [loadProfile]);

  return {
    user,
    profile,
    planKey,
    planObj: getPlan(planKey),
    loading,
    error,
  };
}
